import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CardComp, TableCompData, TabsComp } from "../../../../components";
import TableSkeletonLoader from "../../../../components/Common/TableSkeletonLoader";
import InvestorPortalPageHeader from "../../../../components/Admin/InvestorPortal/InvestorPortalPageHeader";
import InvestorPortalTableEmpty from "../../../../components/Admin/InvestorPortal/InvestorPortalTableEmpty";
import InvestorPortalTableSection, {
  TableRowLink,
} from "../../../../components/Admin/InvestorPortal/InvestorPortalTableSection";
import TableActionDropdown from "../../../../components/Admin/InvestorPortal/TableActionDropdown";
import { OrderStatusBadge } from "../../../../components/Admin/InvestorPortal/AdminStatusBadge";
import { ORDER_QUEUE_TABS } from "../../../../store/admin/marketplace/constants";
import { formatMoney } from "../../../../utils/currencyFormat";
import { convertToTitleCase } from "../../../../utils/helpers";
import { useListStaffMarketplaceOrdersQuery } from "../../../../store/admin/marketplace/staffMarketplaceApi";
import moment from "moment";
import { withMarketplaceGuard } from "../withInvestorPortalGuard";

const PER_PAGE = 10;

function OrdersTable({ statusFilter }) {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");

  const apiQuery = useListStaffMarketplaceOrdersQuery({
    page,
    per_page: PER_PAGE,
    status: statusFilter || undefined,
    search: search || undefined,
  });

  const pageData = useMemo(() => apiQuery.data?.items || [], [apiQuery.data]);
  const apiPagination = apiQuery.data?.pagination || {};
  const pagination = {
    currentPage: apiPagination.current_page || page,
    totalPages: apiPagination.last_page || 1,
    total: apiPagination.total || pageData.length,
    perPage: apiPagination.per_page || PER_PAGE,
    onPageChange: setPage,
  };

  const columns = [
    {
      name: "Order #",
      selector: (row) => row.orderNumber,
      cell: (row) => (
        <TableRowLink
          onClick={() =>
            navigate(
              `/super_admin/investor_portal/marketplace/orders/${encodeURIComponent(row.orderNumber)}`
            )
          }
        >
          {row.orderNumber}
        </TableRowLink>
      ),
      minWidth: "140px",
    },
    {
      name: "Customer",
      selector: (row) => row.customerName || row.applicantName || "—",
      minWidth: "150px",
      grow: 1,
    },
    {
      name: "Type",
      selector: (row) =>
        convertToTitleCase(String(row.customerType || row.accountType || "").replace(/_/g, " ")) ||
        "—",
      minWidth: "130px",
    },
    {
      name: "Items",
      selector: (row) => row.itemsCount ?? row.items?.length ?? 0,
      minWidth: "70px",
      center: true,
    },
    {
      name: "Total",
      selector: (row) => formatMoney(row.totalAmount ?? row.total),
      minWidth: "120px",
      right: true,
    },
    {
      name: "Checkout",
      selector: (row) =>
        convertToTitleCase(String(row.checkoutMode || "").replace(/_/g, " ")) || "—",
      minWidth: "110px",
    },
    {
      name: "Delivery",
      selector: (row) =>
        convertToTitleCase(String(row.deliveryMethod || "").replace(/_/g, " ")) || "—",
      minWidth: "110px",
    },
    {
      name: "Status",
      cell: (row) => <OrderStatusBadge status={row.status} />,
      minWidth: "140px",
    },
    {
      name: "Submitted",
      selector: (row) =>
        row.submittedAt ? moment(row.submittedAt).format("DD-MMM-YY HH:mm") : "—",
      minWidth: "130px",
    },
    {
      name: "Actions",
      cell: (row) => (
        <TableActionDropdown
          items={[
            {
              name: "View details",
              action: () =>
                navigate(
                  `/super_admin/investor_portal/marketplace/orders/${encodeURIComponent(row.orderNumber)}`
                ),
            },
          ]}
          row={row}
        />
      ),
      width: "80px",
      center: true,
    },
  ];

  const emptyState = apiQuery.isError ? (
    <InvestorPortalTableEmpty
      variant="error"
      title="We couldn’t load marketplace orders."
      onCta={() => apiQuery.refetch()}
    />
  ) : statusFilter || search ? (
    <InvestorPortalTableEmpty
      variant="filtered"
      title="No orders match this queue."
      description="Try another status tab or clear your search."
    />
  ) : (
    <InvestorPortalTableEmpty
      variant="empty"
      title="No marketplace orders yet."
      description="Orders appear here when investors book products from the marketplace."
    />
  );

  return (
    <>
      <CardComp cardClassName="border-0 shadow-sm mb-3 investor-portal-filter-bar">
        <div className="row g-2 align-items-end">
          <div className="col-12 col-md-6">
            <label className="form-label small fw-semibold">Search</label>
            <input
              className="form-control"
              placeholder="Search order number, customer, phone..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
          </div>
        </div>
      </CardComp>

      <InvestorPortalTableSection pagination={pagination}>
        <TableCompData
          columns={columns}
          data={pageData}
          progressPending={apiQuery.isFetching}
          progressComponent={<TableSkeletonLoader rows={8} columns={10} />}
          noDataComponent={emptyState}
        />
      </InvestorPortalTableSection>
    </>
  );
}

function OrdersListPage() {
  const useTab = ORDER_QUEUE_TABS.map((tab) => ({
    name: tab.label,
    component: <OrdersTable statusFilter={tab.key} />,
  }));

  return (
    <div className="pb-4">
      <InvestorPortalPageHeader
        title="Marketplace Orders"
        subtitle="Review, confirm, and fulfill investor marketplace orders."
      />
      <TabsComp
        TabArray={useTab}
        input="name"
        justifyContent="justify-content-start"
        tabClassName="px-0"
        TabArrayClassName="px-1"
      />
    </div>
  );
}

export default withMarketplaceGuard(OrdersListPage);
